import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from 'chart.js';

import startPoint from '../assets/svgs/starting-point.svg';
import finishPoint from '../assets/svgs/finish-point.svg';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const Container = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 40px;
  color: #385963;
  font-weight: 600;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 25px;
  }
`;

const Description = styled.div`
  font-size: 20px;
  margin-bottom: 20px;
  color: #91abb3;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ContainerForMap = styled.div`
  width: 100%;
  height: 60vh;
  border-radius: 10px;
  margin-bottom: 100px;

  @media (max-width: 768px) {
    height: 50vh;

  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  max-width: 100%;
`;

const FitBoundsToPath = ({ gpxPoints }) => {
  const map = useMap();

  useEffect(() => {
    if (gpxPoints && gpxPoints.length > 0) {
      const latlngs = gpxPoints.map(p => [p.latitude, p.longitude]);
      map.fitBounds(latlngs);
    }
  }, [gpxPoints, map]);

  return null;
};

const ElevationChart = ({ points }) => {
  const data = {
    labels: points.map((_, i) => `P${i + 1}`),
    datasets: [
      {
        label: 'Elevation (m)',
        data: points.map(p => p.elevation),
        fill: false,
        borderColor: '#0070f3',
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    scales: {
      x: { display: false },
      y: {
        beginAtZero: false,
        title: { display: true, text: 'Elevation (m)' },
      },
    },
  };

  return (
    <ChartWrapper>
      <Line data={data} options={options} />
    </ChartWrapper>
  );
};

const ExperiencePath = ({ gpxPoints = [] }) => {
  const { i18n } = useTranslation();

  const validPoints = gpxPoints.filter(
    p => typeof p.latitude === 'number' && typeof p.longitude === 'number'
  );

  if (validPoints.length === 0) return <div></div>;

  const polylinePoints = validPoints.map(p => [p.latitude, p.longitude]);

  const startIcon = L.icon({
    iconUrl: startPoint,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

  const endIcon = L.icon({
    iconUrl: finishPoint,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

  // Check if start and finish points are identical;
const start = polylinePoints[0];
const finish = polylinePoints[polylinePoints.length - 1];

// Check if start and finish are exactly the same location
const isSameLocation = start[0] === finish[0] && start[1] === finish[1];

// If overlapping, offset the finish marker a little bit
const finishPosition = isSameLocation
  ? [finish[0] + 0.00005, finish[1] + 0.00005] // slightly northeast
  : finish;

  const hasNonZeroElevation = validPoints.some(p => p.elevation && p.elevation !== 0);


const FitBoundsToPath = ({ gpxPoints }) => {
  const map = useMap();

  useEffect(() => {
    if (gpxPoints && gpxPoints.length >= 2) {
      const latlngs = gpxPoints.map(p => [p.latitude, p.longitude]);
      map.fitBounds(latlngs);
    } else if (gpxPoints && gpxPoints.length === 1) {
      const { latitude, longitude } = gpxPoints[0];
      map.setView([latitude, longitude], 13); // fallback: center + zoom
    }
  }, [gpxPoints, map]);

  return null;
};


  return (
    <Container>
      <Title>{i18n.t('experiencePath')}</Title>
      <Description>{i18n.t('experiencePathDescription')}</Description>
      <ContainerForMap>
        <MapContainer
          center={start}
          zoom={13}
          style={{ height: '100%', width: '100%', borderRadius: '10px' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          <Polyline positions={polylinePoints} color="blue" weight={4} />
          <Marker position={start} icon={startIcon}>
            <Popup>Start</Popup>
          </Marker>
          {/* <Marker position={finish} icon={endIcon} >
              <Popup>Finish</Popup>
          </Marker> */}
          <FitBoundsToPath gpxPoints={validPoints} />
        </MapContainer>
      </ContainerForMap>

        {hasNonZeroElevation && (
        <>
          <Title style={{ marginBottom: '20px' }}>{i18n.t('elevationChart')}</Title>
          <ElevationChart points={validPoints} />
        </>
      )}
    </Container>
  );
};



export default ExperiencePath;
